// bracket-highlighter.js - Purple highlight for [text] syntax
// Custom syntax for purple/lila marker highlighting

import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin } from "@codemirror/view";

// Decoration for the content (purple background)
const bracketContent = Decoration.mark({
    class: "cm-bracket-content"
});

// Decoration for the [ ] markers (hidden by default)
const bracketMarkerHidden = Decoration.mark({
    class: "cm-bracket-marker cm-bracket-marker-hidden"
});

// Decoration for the [ ] markers (visible when cursor inside)
const bracketMarkerVisible = Decoration.mark({
    class: "cm-bracket-marker"
});

function findBrackets(view) {
    const doc = view.state.doc;
    const text = doc.toString();
    const cursorPos = view.state.selection.main.head;

    const decorations = [];

    // Find all [text] patterns that are NOT followed by ( (to avoid link syntax)
    // Match [text] where text doesn't contain [ or ] or newlines
    const regex = /\[([^\[\]\n]+)\](?!\()/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
        const fullStart = match.index;
        const fullEnd = match.index + match[0].length;
        const contentStart = fullStart + 1;
        const contentEnd = fullEnd - 1;

        // Check if cursor is inside this bracket (including markers)
        const cursorInside = cursorPos >= fullStart && cursorPos <= fullEnd;
        const markerDeco = cursorInside ? bracketMarkerVisible : bracketMarkerHidden;

        // Opening [
        decorations.push({ from: fullStart, to: contentStart, deco: markerDeco });
        // Content with purple background
        decorations.push({ from: contentStart, to: contentEnd, deco: bracketContent });
        // Closing ]
        decorations.push({ from: contentEnd, to: fullEnd, deco: markerDeco });
    }

    // Sort by position and build RangeSet
    decorations.sort((a, b) => a.from - b.from);

    const builder = new RangeSetBuilder();
    for (const { from, to, deco } of decorations) {
        builder.add(from, to, deco);
    }

    return builder.finish();
}

export const bracketHighlighter = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = findBrackets(view);
    }

    update(update) {
        // Update on doc changes, viewport changes, or selection changes (cursor moved)
        if (update.docChanged || update.viewportChanged || update.selectionSet) {
            this.decorations = findBrackets(update.view);
        }
    }
}, {
    decorations: v => v.decorations
});
